/**
 * Process Item Drawer Component
 * 
 * Complete drawer solution for adding/editing processes including:
 * - Form component with process details
 * - Search mode for selecting process owner
 * - Automatic process code assignment
 * - Form validation
 * 
 * File Upload Flow:
 * 1. User selects files → Stored as File objects in local state (no upload yet)
 * 2. When form is submitted:
 *    - ADD MODE: Upload all selected files to get fileIds
 *    - EDIT MODE: 
 *      - Compare current files with initial files
 *      - Upload new files to get fileIds
 *      - Delete removed files from backend
 *      - Combine fileIds (existing + new)
 * 3. Pass documentIds and testSheetId to onSubmit
 * 4. This prevents:
 *    - Unnecessary uploads if user cancels
 *    - Data loss from premature deletions
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Upload, Button, Select, message, Tooltip } from 'antd';
import styled from 'styled-components';
import { SearchOutlined, RightOutlined, LoadingOutlined, UploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import CloseCircleIcon from '../../../assets/icons/ic-close-circle.svg?react';
import LinkIcon from '../../../assets/icons/ic-link.svg?react';
import {
    searchProcessOwners,
    ProcessOwner,
} from '../../../services/hierarchyService';
import {
    uploadProcessDocument,
    uploadProcessTestSheet,
    deleteProcessDocument,
    deleteProcessTestSheet,
    downloadFile
} from '../../../services/fileService';
import ButtonGroup from '../../../components/common/ButtonGroup';
import { FileUpload, RiskRanking } from '../../../services/types';
import { ALLOWED_DOCUMENT_EXTENSIONS } from '../../../utils/constants';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

// ========== TYPE DEFINITIONS ==========

// FileSelect can be either a new file (File object) or existing file (with fileId)
export interface FileSelect {
    file?: File; // File object if not uploaded yet
    fileId?: string; // Backend file ID if already uploaded
    name: string; // Display name
}

export interface ProcessFormData {
    id?: string; // Optional id for edit mode
    processCode?: string; // Auto-generated for add mode, read-only for edit mode
    processName: string;
    processWeight: number;
    riskRanking: RiskRanking;
    processOwner?: ProcessOwner | null;
    documents: FileUpload[];
    testSheet?: FileUpload;
    totalGaps?: number; // Read-only, calculated from gaps array
}

export interface ProcessItemDrawerProps {
    open: boolean;
    mode: 'add' | 'edit';
    initialValues?: Partial<ProcessFormData>;
    nextProcessCode?: string; // For add mode: the next process code (A01, A02...)
    autoEnterSearchMode?: boolean; // Auto enter search mode on open (for missing owner)
    onClose: () => void;
    onSubmit: (values: ProcessFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  .info-icon {
    color: #9CA3AF;
    font-size: 14px;
    cursor: help;

    &:hover {
      color: #6B7280;
    }
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledInput = styled(Input)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  height: 44px;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }

  &:disabled {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  height: 44px;
  border-radius: 8px !important;

  .ant-input-number-input {
    height: 42px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    letter-spacing: 0.005em;
    color: #6B7280;

    &::placeholder {
      color: #9CA3AF;
    }
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  height: 44px;

  .ant-select-selector {
    height: 44px !important;
    border-radius: 8px !important;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    letter-spacing: 0.005em;
    color: #6B7280;
    display: flex;
    align-items: center;

    &::placeholder {
      color: #9CA3AF;
    }
  }

  .ant-select-selection-item {
    line-height: 42px !important;
  }

  &.ant-select-disabled .ant-select-selector {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

const CharacterCounter = styled.div<{ $isNearLimit?: boolean }>`
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  color: ${props => props.$isNearLimit ? '#EF4444' : '#9CA3AF'};
  margin-top: 4px;
  text-align: right;
`;

// Search UI Components
const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SearchInputWrapper = styled.div`
  margin-bottom: 24px;

  .ant-input-affix-wrapper {
    height: 44px;
    border-radius: 6px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;

    .ant-input {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    .anticon-search {
      color: #6B7280;
    }
  }
`;

const SearchResultsList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SearchResultItem = styled.div<{ $selected: boolean }>`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'transparent'};
  border: 1px solid ${props => props.$selected ? '#2563EB' : '#E5E7EB'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }

  .item-label {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
  }

  .item-sublabel {
    font-family: Inter;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #6B7280;
    margin-top: 4px;
  }
`;

const TagContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 32px 8px 12px;
  min-height: 44px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #2563EB;
  }

  &:focus {
    border-color: #2563EB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }
`;

const ArrowButton = styled.div`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  
  .anticon {
    color: #6B7280;
    font-size: 12px;
    transition: color 0.2s;
  }

  &:hover .anticon {
    color: #2563EB;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

const Tag = styled.span<{
    $backgroundColor: string;
    $color: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${props => props.$backgroundColor};
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$color};
  cursor: default;
  transition: all 0.2s;
`;

const StyledCloseIcon = styled(CloseCircleIcon) <{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
`;

const StyledUpload = styled(Upload)`
  .ant-btn {
    height: 44px;
    border-radius: 8px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #2563EB;
    border-color: #2563EB;

    &:hover {
      color: #1D4ED8 !important;
      border-color: #1D4ED8 !important;
    }
  }
`;

const FileListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const FileItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s;

  &:hover {
    opacity: 0.9;
  }

  .file-icon {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }

  .file-name {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .delete-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.1);
    }

    path {
      stroke: #1E40AF;
    }
  }
`;

const FileLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const FileNoLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s;
  white-space: nowrap;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

// ========== MAIN COMPONENT ==========

const ProcessItemDrawer: React.FC<ProcessItemDrawerProps> = ({
    open,
    mode,
    initialValues,
    nextProcessCode,
    autoEnterSearchMode = false,
    onClose,
    onSubmit,
}) => {
    // Form states
    const [form] = Form.useForm();
    const [processCode, setProcessCode] = useState('');
    const [processName, setProcessName] = useState('');
    const [processWeight, setProcessWeight] = useState<number | undefined>(undefined);
    const [riskRanking, setRiskRanking] = useState<RiskRanking>('Unranked');
    const [processOwner, setProcessOwner] = useState<ProcessOwner | null>(null);
    const { isExternalUser } = useSelector((state: RootState) => state.auth);
    //const { isAdmin } = useSelector((state: RootState) => state.auth);
    // File states - FileSelect can have either file (new) or fileId (existing)
    const [documents, setDocuments] = useState<FileSelect[]>([]);
    const [testSheet, setTestSheet] = useState<FileSelect | undefined>(undefined);
    
    const [totalGaps, setTotalGaps] = useState<number>(0);

    // UI states
    const [loading, setLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [tempSelectedOwner, setTempSelectedOwner] = useState<ProcessOwner | null>(null);

    // Search results states
    const [ownerResults, setOwnerResults] = useState<ProcessOwner[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Reset form when initialValues change
    useEffect(() => {
        if (mode === 'edit' && initialValues) {
            // Edit mode - load existing values
            setProcessCode(initialValues.processCode || '');
            setProcessName(initialValues.processName || '');
            setProcessWeight(initialValues.processWeight);
            setRiskRanking(initialValues.riskRanking || 'Unranked' as RiskRanking);
            setProcessOwner(initialValues.processOwner || null);

            // Load existing files (convert FileUpload to FileSelect with fileId)
            setDocuments((initialValues.documents || []).map(doc => ({
                fileId: doc.id,
                name: doc.name,
            })));
            setTestSheet(initialValues.testSheet ? {
                fileId: initialValues.testSheet.id,
                name: initialValues.testSheet.name,
            } : undefined);
            
            setTotalGaps(initialValues.totalGaps || 0);

            form.setFieldsValue({
                processCode: initialValues.processCode,
                processName: initialValues.processName,
                processWeight: initialValues.processWeight,
                riskRanking: initialValues.riskRanking,
                processOwner: initialValues.processOwner,
                totalGaps: initialValues.totalGaps || 0,
            });

      
        } else {
            // Add mode - reset to empty
            resetForm();
            // Set the next process code for display
            if (nextProcessCode) {
                setProcessCode(nextProcessCode);
                form.setFieldsValue({
                    processCode: nextProcessCode,
                });
            }
        }
    }, [open, mode, initialValues, nextProcessCode, autoEnterSearchMode]);

    useEffect(() => {
        if (open && autoEnterSearchMode) {
            setTimeout(() => {
                setIsSearchMode(true);
                setTempSelectedOwner(initialValues?.processOwner || null);
            }, 200);
        }
    }, [open, autoEnterSearchMode]);

    // Watch for field changes and validate
    useEffect(() => {
        const checkFormValidation = () => {
            const hasName = !!processName && processName.trim() !== '';
            const hasWeight = processWeight != null && processWeight >= 0;

            const isValid = hasName && hasWeight;
            setIsFormValid(isValid);
        };

        checkFormValidation();
    }, [processName, processWeight]);

    const resetForm = () => {
        setProcessCode('');
        setProcessName('');
        setProcessWeight(undefined);
        setRiskRanking('Unranked' as RiskRanking);
        setProcessOwner(null);
        setDocuments([]);
        setTestSheet(undefined);
        setTotalGaps(0);
        form.resetFields();
    };

    // Search mode handlers
    const enterSearchMode = () => {
        setIsSearchMode(true);
        setSearchQuery('');
        setTempSelectedOwner(processOwner);
        // Clear previous results
        setOwnerResults([]);
    };

    const exitSearchMode = () => {
        setIsSearchMode(false);
        setSearchQuery('');
        setTempSelectedOwner(null);
        setOwnerResults([]);
    };

    // Fetch search results when query changes
    useEffect(() => {
        const fetchSearchResults = async () => {
            if (!isSearchMode) return;

            const companyId = localStorage.getItem("companyId");
            // Need companyId from user
            if (!companyId) {
                console.error('No companyId available');
                setOwnerResults([]);
                return;
            }

            setIsSearching(true);

            try {
                const results = await searchProcessOwners({
                    companyId: companyId ?? "",
                    searchTerm: searchQuery
                });
                setOwnerResults(results);
            } catch (error) {
                console.error('Search failed:', error);
                setOwnerResults([]);
            } finally {
                setIsSearching(false);
            }
        };

        // Debounce search (wait 300ms after user stops typing)
        const timeoutId = setTimeout(fetchSearchResults, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery, isSearchMode]);

    const handleSearchResultClick = (owner: ProcessOwner) => {
        // Toggle selection
        if (tempSelectedOwner?.userId === owner.userId) {
            setTempSelectedOwner(null);
        } else {
            setTempSelectedOwner(owner);
        }
    };

    const applySearchSelection = () => {
        setProcessOwner(tempSelectedOwner);
        form.setFieldValue('processOwner', tempSelectedOwner);
        exitSearchMode();
    };

    // Handle document selection (not upload yet)
    const handleDocumentSelect = (file: File): boolean => {
        // Check if file already exists (by name and size)
        const isDuplicate = documents.some(doc => 
            doc.name === file.name && 
            doc.file?.size === file.size
        );

        if (isDuplicate) {
            message.warning(`File "${file.name}" has already been selected`);
            return false;
        }

        // Add file to documents list (without fileId = new file)
        setDocuments(prev => [...prev, {
            file: file,
            name: file.name,
        }]);
        return false; // Prevent default upload behavior
    };

    // Handle document delete
    const handleDocumentDelete = (fileSelect: FileSelect) => {
        // Remove from documents list (will be compared with initialValues in submit)
        setDocuments(prev => prev.filter(f => f !== fileSelect));
    };

    // Handle test sheet selection (not upload yet)
    const handleTestSheetSelect = (file: File): boolean => {
        // Only allow one test sheet
        if (testSheet) {
            message.warning('Only one test sheet is allowed per process');
            return false;
        }

        setTestSheet({
            file: file,
            name: file.name,
        });
        return false; // Prevent default upload behavior
    };

    // Handle test sheet delete
    const handleTestSheetDelete = () => {
        setTestSheet(undefined);
    };

    const [isDownloading, setIsDownloading] = useState(false);
    
    const handleDownload = async (e: React.MouseEvent, fileSelect: FileSelect) => {
        e.stopPropagation();
        e.preventDefault();
        if (isDownloading) {
            return;
        }

        setIsDownloading(true);
        try {
            let fileId = fileSelect.fileId ?? "";
            let fileName = fileSelect.name;

            if (fileSelect.fileId)
                await downloadFile(fileId, fileName);
        } catch (error) {
            console.error('Failed to download file:', error);
            message.error('Failed to download file');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleClose = () => {
        setIsFormValid(false);
        setIsSearchMode(false);
        resetForm();
        onClose();
    };

    // Handle drawer close button (X) - context-aware
    const handleDrawerClose = () => {
        if (isSearchMode) {
            exitSearchMode();
        } else {
            handleClose();
        }
    };

    const handleFormSubmit = async () => {
        setLoading(true);
        try {
            let finalDocuments: FileUpload[] = [];
            let finalTestSheet: FileUpload | undefined = undefined;

            // Upload files that don't have fileId (new files)
            const uploadedDocs = await Promise.all(
                documents.map(async (doc) => {
                    if (doc.fileId) {
                        // Already uploaded - keep existing fileId
                        return { id: doc.fileId, name: doc.name };
                    } else if (doc.file) {
                        // New file - upload it
                        try {
                            const response = await uploadProcessDocument(doc.file);
                            return { id: response.data.id, name: doc.name };
                        } catch (error) {
                            console.error(`Failed to upload ${doc.name}:`, error);
                            message.error(`Failed to upload ${doc.name}`);
                            return null;
                        }
                    }
                    return null;
                })
            );
            finalDocuments = uploadedDocs.filter(doc => doc !== null) as FileUpload[];

            // Upload test sheet if new
            if (testSheet) {
                if (testSheet.fileId) {
                    // Already uploaded - keep existing fileId
                    finalTestSheet = { id: testSheet.fileId, name: testSheet.name };
                } else if (testSheet.file) {
                    // New file - upload it
                    try {
                        const response = await uploadProcessTestSheet(testSheet.file);
                        finalTestSheet = { id: response.data.id, name: testSheet.name };
                    } catch (error) {
                        console.error('Failed to upload test sheet:', error);
                        message.error('Failed to upload test sheet');
                    }
                }
            }

            // EDIT MODE: Delete removed files
            if (mode === 'edit' && initialValues?.id) {
                const processId = initialValues.id;

                // Find removed documents (in initialValues but not in current documents)
                const initialDocIds = (initialValues.documents || []).map(d => d.id);
                const currentDocIds = documents.filter(d => d.fileId).map(d => d.fileId!);
                const removedDocIds = initialDocIds.filter(id => !currentDocIds.includes(id));

                // Delete removed documents
                await Promise.all(
                    removedDocIds.map(async (fileId) => {
                        try {
                            await deleteProcessDocument(processId, fileId);
                        } catch (error) {
                            console.error(`Failed to delete document ${fileId}:`, error);
                        }
                    })
                );

                // Check if test sheet was removed
                if (initialValues.testSheet && !testSheet) {
                    try {
                        await deleteProcessTestSheet(processId, initialValues.testSheet.id);
                    } catch (error) {
                        console.error('Failed to delete test sheet:', error);
                    }
                } else if (initialValues.testSheet && testSheet?.fileId && 
                          initialValues.testSheet.id !== testSheet.fileId) {
                    // Test sheet was replaced - delete old one
                    try {
                        await deleteProcessTestSheet(processId, initialValues.testSheet.id);
                    } catch (error) {
                        console.error('Failed to delete old test sheet:', error);
                    }
                }
            }

            // Submit form with file IDs
            await onSubmit({
                id: mode === 'edit' ? initialValues?.id : undefined,
                processCode: mode === 'add' ? nextProcessCode : processCode,
                processName,
                processWeight: processWeight ?? 0,
                riskRanking,
                processOwner,
                documents: finalDocuments,
                testSheet: finalTestSheet,
            });

            handleClose();
        } catch (error) {
            console.error("Form submission failed:", error);
        } finally {
            setLoading(false);
        }
    };

    // Render selected process owner tag
    const renderProcessOwnerTag = () => {
        if (!processOwner) {
            return (
                <span style={{
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#9CA3AF'
                }}>
                    Search Process Owner
                </span>
            );
        }

        return (
            <Tag
                $backgroundColor="#F8FAFC"
                $color="#1F2937"
            >
                {processOwner.userName}
                <StyledCloseIcon
                    $circleFill="#E5E7EB"
                    $strokeColor="#6B7280"
                    onClick={(e) => {
                        e.stopPropagation();
                        setProcessOwner(null);
                        form.setFieldValue('processOwner', null);
                    }}
                />
            </Tag>
        );
    };

    // Render search UI
    const renderSearchUI = () => {
        return (
            <SearchContainer>
                <SearchInputWrapper>
                    <Input
                        prefix={<SearchOutlined />}
                        suffix={
                            isSearching && searchQuery !== '' ? (
                                <LoadingOutlined style={{ color: '#2563EB' }} />
                            ) : null
                        }
                        placeholder="Search Process Owner..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                </SearchInputWrapper>

                <SearchResultsList>
                    {ownerResults.length === 0 ? (
                        searchQuery === '' ? (
                            <EmptyState>{isSearching ? 'Loading...' : 'There are no process owners to display'}</EmptyState>
                        ) : (
                            <EmptyState>{isSearching ? 'Searching...' : 'There are no results found'}</EmptyState>
                        )
                    ) : (
                        ownerResults.map((owner) => {
                            const isSelected = tempSelectedOwner?.userId === owner.userId;
                            return (
                                <SearchResultItem
                                    key={owner.userId}
                                    $selected={isSelected}
                                    onClick={() => handleSearchResultClick(owner)}
                                >
                                    <div className="item-label">{owner.userName}</div>
                                    <div className="item-sublabel">{owner.title}</div>
                                </SearchResultItem>
                            );
                        })
                    )}
                </SearchResultsList>
            </SearchContainer>
        );
    };

    // Render form UI
    const renderFormUI = () => (
        <StyledForm
            id="process-form"
            form={form}
            layout="vertical"
            requiredMark={false}
        >
            {/* Process Code (Read-only, auto-generated) */}
            <Form.Item name="processCode">
                <div>
                    <FormLabel>Process Code</FormLabel>
                    <StyledInput
                        disabled
                        value={processCode}
                        placeholder="Auto-generated"
                    />
                    <InfoText>
                        {mode === 'add'
                            ? 'Code will be automatically assigned based on the cycle'
                            : 'Process code cannot be changed'}
                    </InfoText>
                </div>
            </Form.Item>

            {/* Process Name */}
            <Form.Item
                name="processName"
                rules={[
                    { required: true, message: 'Please enter a process name' },
                    { whitespace: true, message: 'Process name cannot be empty' },
                ]}
            >
                <div>
                    <FormLabel>Process Name</FormLabel>
                    <StyledInput
                        value={processName}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 100 characters
                            if (value.length <= 100) {
                                setProcessName(value);
                            }
                        }}
                        placeholder="Enter process name"
                        maxLength={100}
                        spellCheck={true}
                    />
                    <CharacterCounter $isNearLimit={processName.length >= 90}>
                        {processName.length}/100
                    </CharacterCounter>
                </div>
            </Form.Item>

            {/* Process Weight */}
            <Form.Item
                name="processWeight"
                rules={[
                    {
                        validator: (_, value) => {
                            if (value === null || value === undefined || value === '') {
                                return Promise.reject('Please enter a process weight');
                            }
                            const numValue = typeof value === 'string' ? parseFloat(value) : value;
                            if (isNaN(numValue)) {
                                return Promise.reject('Weight must be a number');
                            }
                            if (numValue < 0) {
                                return Promise.reject('Weight must be positive');
                            }
                            return Promise.resolve();
                        },
                    },
                ]}
            >
                <div>
                    <FormLabel>
                        Process Weight
                        <Tooltip title="This weight represents the Process's impact on its parent Cycle. All Process weights within this Cycle must sum to 100%.">
                            <InfoCircleOutlined className="info-icon" />
                        </Tooltip>
                    </FormLabel>
                    <StyledInputNumber
                        type="number"
                        step={1}
                        precision={0}
                        value={processWeight}
                        onChange={(value) => {
                            if (value === null || value === undefined) {
                                setProcessWeight(undefined);
                            } else {
                                setProcessWeight(typeof value === 'string' ? parseFloat(value) : value);
                            }
                        }}
                        placeholder="Enter weight of Process as % of Cycle"
                        controls={false}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    />
                </div>
            </Form.Item>

            {/* Risk Ranking */}
            <Form.Item name="riskRanking">
                <div>
                    <FormLabel>Risk Ranking</FormLabel>
                    <StyledSelect
                        value={riskRanking}
                        onChange={(value) => setRiskRanking(value as RiskRanking)}
                        disabled={true}
                        options={[
                            { label: 'Unranked', value: 'Unranked' },
                            { label: 'Low', value: 'Low' },
                            { label: 'Med', value: 'Med' },
                            { label: 'High', value: 'High' },
                        ]}
                    />
                    <InfoText>
                        Risk ranking is read-only and managed through the Risk Assessment module
                    </InfoText>
                </div>
            </Form.Item>

            {/* Total GAPs */}
            {mode === 'edit' && (
                <Form.Item name="totalGaps">
                    <div>
                        <FormLabel>Total GAPs</FormLabel>
                        <StyledInputNumber
                            value={totalGaps}
                            disabled={true}
                            formatter={(value) => `${value || 0}`}
                        />
                        <InfoText>
                            Total GAPs is read-only and managed through the GAPs Management module
                        </InfoText>
                    </div>
                </Form.Item>
            )}

            <Form.Item name="processOwner">
                <div>
                    <FormLabel>Process Owner</FormLabel>
                    <TagContainer
                        tabIndex={0}
                        onClick={() => {
                            if (!processOwner) {
                                enterSearchMode();
                            }
                        }}
                        onFocus={() => {
                            // Auto enter search mode when focused via Tab
                            enterSearchMode();
                        }}
                        onKeyDown={(e) => {
                            // Enter search mode on Enter or Space key
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                enterSearchMode();
                            }
                        }}
                    >
                        {renderProcessOwnerTag()}
                        <ArrowButton
                            onClick={(e) => {
                                e.stopPropagation();
                                enterSearchMode();
                            }}
                        >
                            <RightOutlined />
                        </ArrowButton>
                    </TagContainer>
                </div>
            </Form.Item>

            {/* Flowcharts */}
            <Form.Item name="flowcharts">
                <div>
                    <FormLabel>Flowcharts</FormLabel>
                    <StyledUpload
                        fileList={[]}
                        beforeUpload={handleDocumentSelect}
                        showUploadList={false}
                        multiple
                        accept={ALLOWED_DOCUMENT_EXTENSIONS}
                        disabled={loading}
                    >
                        <Button
                            icon={<UploadOutlined />}
                            disabled={loading}
                        >
                            Select Flowcharts
                        </Button>
                    </StyledUpload>

                    {documents.length > 0 && (
                        <FileListWrapper>
                            {documents.map((fileSelect, index) => (
                                <FileItem key={fileSelect.fileId || `new-${index}`}>
                                    {fileSelect.fileId && 
                                        <FileLink className="file-name" title={fileSelect.name} onClick={(event:React.MouseEvent) => handleDownload(event, fileSelect)}>
                                            {isDownloading ? (
                                                <LoadingOutlined style={{ fontSize: 12 }} spin />
                                            ) : (
                                                <LinkIcon />
                                            )}
                                            {fileSelect.name}
                                        </FileLink>
                                    }
                                    {!fileSelect.fileId && 
                                        <FileNoLink className="file-name" title={fileSelect.name}>
                                            {fileSelect.name + ' (new)'}
                                        </FileNoLink>
                                    }
                                    <StyledCloseIcon
                                        $circleFill="#F8FAFC"
                                        $strokeColor="#1E40AF"
                                        onClick={() => handleDocumentDelete(fileSelect)}
                                    />
                                </FileItem>
                            ))}
                        </FileListWrapper>
                    )}
                </div>
            </Form.Item>

            {/* Test Sheets */}
            <Form.Item name="testSheets">
                <div>
                    <FormLabel>Test Sheets</FormLabel>
                    <StyledUpload
                        fileList={[]}
                        beforeUpload={handleTestSheetSelect}
                        showUploadList={false}
                        multiple={false}
                        maxCount={1}
                        accept={ALLOWED_DOCUMENT_EXTENSIONS}
                        disabled={loading || testSheet !== undefined}
                    >
                        <Button
                            icon={<UploadOutlined />}
                            disabled={loading || testSheet !== undefined}
                        >
                            Select Test Sheet
                        </Button>
                    </StyledUpload>

                    {testSheet && (
                        <FileListWrapper>
                            <FileItem>
                                {testSheet.fileId && 
                                        <FileLink className="file-name" title={testSheet.name} onClick={(event:React.MouseEvent) => handleDownload(event, testSheet)}>
                                            {isDownloading ? (
                                                <LoadingOutlined style={{ fontSize: 12 }} spin />
                                            ) : (
                                                <LinkIcon />
                                            )}
                                            {testSheet.name}
                                        </FileLink>
                                    }
                                    {!testSheet.fileId && 
                                        <FileNoLink className="file-name" title={testSheet.name}>
                                            {testSheet.name + ' (new)'}
                                        </FileNoLink>
                                    }
                                <StyledCloseIcon
                                    $circleFill="#F8FAFC"
                                    $strokeColor="#1E40AF"
                                    onClick={handleTestSheetDelete}
                                />
                            </FileItem>
                        </FileListWrapper>
                    )}
                </div>
            </Form.Item>
        </StyledForm>
    );

    const drawerTitle = isSearchMode ? 'Edit Process Owner' : (mode === 'add' ? 'Add Process' : 'Edit Process');

    return (
        <RightContentDrawer
            open={open}
            title={drawerTitle}
            width={572}
            onClose={handleDrawerClose}
            footer={
                isSearchMode ? (
                    // Search mode footer: Go back + Done buttons
                    <ButtonGroup>
                        <Button type="default" onClick={exitSearchMode}>
                            Go back
                        </Button>
                        <Button type="primary" onClick={applySearchSelection}>
                            Done
                        </Button>
                    </ButtonGroup>
                ) : (
                    // Normal mode footer: Go back + Submit buttons
                    <ButtonGroup>
                        <Button type="default" onClick={handleClose} disabled={loading}>
                            Go back
                        </Button>
                        <Button
                            type="primary"
                            loading={loading}
                            disabled={!isFormValid || isExternalUser}
                            form="process-form"
                            onClick={handleFormSubmit}
                        >
                            {mode === 'add' ? 'Add Process' : 'Save changes'}
                        </Button>
                    </ButtonGroup>
                )
            }
        >
            {isSearchMode ? renderSearchUI() : renderFormUI()}
        </RightContentDrawer>
    );
};

export default ProcessItemDrawer;

